import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Leaf } from "lucide-react";
import api from "@/lib/api";
import { CtaButton } from "@/components/CtaButton";
import { SiteHeaderMobileMenu } from "@/components/SiteHeaderMobileMenu";
import { SITE } from "@/constants/testIds";

const NAV_LINKS = [
    { label: "About", to: "/about" },
    { label: "Treatments", to: "/treatments" },
    { label: "Packages", to: "/packages" },
    { label: "Why Ayurveda", to: "/why-ayurveda" },
    { label: "Blog", to: "/blog" },
    { label: "FAQ", to: "/faq" },
    { label: "Shop", to: "/shop" },
    { label: "Contact", to: "/contact" },
];

const MOBILE_LINKS = [
    { label: "Home", to: "/" },
    ...NAV_LINKS,
    { label: "Testimonials", to: "/testimonials" },
];

// Header background class lookup (avoids nested ternaries)
const HEADER_BG = {
    solid:
        "bg-cream/95 backdrop-blur supports-[backdrop-filter]:bg-cream/85 border-b border-border/80 shadow-[0_4px_18px_rgba(30,35,32,0.05)]",
    transparent:
        "bg-cream/70 backdrop-blur supports-[backdrop-filter]:bg-cream/60 border-b border-transparent",
};

/** Small piece: brand block (logo + tagline). */
function HeaderBrand({ info }) {
    return (
        <Link
            to="/"
            className="flex items-center gap-2 group shrink-0"
            data-testid="site-header-logo"
        >
            {info?.logo_url ? (
                <img
                    src={info.logo_url}
                    alt="Choice Ayurveda"
                    className="h-10 sm:h-12 w-auto"
                />
            ) : (
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-moss text-cream">
                    <Leaf className="h-4 w-4" strokeWidth={1.75} />
                </span>
            )}
            <div className="hidden sm:block leading-tight">
                <p className="font-display text-lg text-moss group-hover:text-terracotta transition-colors">
                    Choice Ayurveda
                </p>
                <p className="text-[0.65rem] uppercase tracking-[0.18em] text-foreground/55 font-accent">
                    Maui &middot; Online
                </p>
            </div>
        </Link>
    );
}

/** Small piece: desktop nav links. */
function HeaderDesktopNav() {
    return (
        <nav
            data-testid="site-header-nav"
            aria-label="Primary"
            className="hidden lg:flex items-center gap-1"
        >
            {NAV_LINKS.map((l) => (
                <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`site-header-link-${l.to.replace("/", "")}`}
                    className={({ isActive }) =>
                        `relative px-3 py-2 text-sm font-medium transition-colors ${
                            isActive
                                ? "text-terracotta"
                                : "text-foreground/80 hover:text-moss"
                        }`
                    }
                >
                    {({ isActive }) => (
                        <>
                            {l.label}
                            {isActive && (
                                <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 rounded-full bg-terracotta" />
                            )}
                        </>
                    )}
                </NavLink>
            ))}
        </nav>
    );
}

/** Small piece: right cluster (phone, CTAs, hamburger). */
function HeaderActions({ info, open, onToggle }) {
    return (
        <div className="flex items-center gap-2 sm:gap-3">
            {info?.phone_link && (
                <a
                    href={info.phone_link}
                    className="hidden md:inline-flex items-center gap-1.5 text-sm text-foreground/75 hover:text-terracotta"
                    aria-label={`Call ${info?.phone || ""}`}
                >
                    <Phone className="h-4 w-4" strokeWidth={1.75} />
                    <span className="hidden xl:inline">{info?.phone}</span>
                </a>
            )}
            <CtaButton
                to="/intake"
                variant="accent"
                icon={false}
                className="hidden md:inline-flex"
                testid={SITE.bookConsultationCta}
            >
                New client intake
            </CtaButton>
            <CtaButton
                to="/contact"
                variant="primary"
                icon={false}
                className="hidden sm:inline-flex"
            >
                Book
            </CtaButton>
            <button
                type="button"
                className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sand/80 text-moss hover:bg-sand"
                onClick={onToggle}
                aria-label="Toggle menu"
                aria-expanded={open}
                data-testid="site-header-mobile-toggle"
            >
                {open ? (
                    <X className="h-5 w-5" strokeWidth={1.75} />
                ) : (
                    <Menu className="h-5 w-5" strokeWidth={1.75} />
                )}
            </button>
        </div>
    );
}

/**
 * Sticky global site header.
 * - Cream background that solidifies on scroll
 * - Logo (left) + nav links (centre) + phone + CTAs (right)
 * - Mobile menu is rendered by <SiteHeaderMobileMenu/> in its own file
 */
export const SiteHeader = () => {
    const [info, setInfo] = useState(null);
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        let cancelled = false;
        api.siteInfo()
            .then((data) => {
                if (!cancelled) setInfo(data);
            })
            .catch((err) => console.warn("SiteHeader: failed to load site info:", err));
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const bgClass = scrolled ? HEADER_BG.solid : HEADER_BG.transparent;

    return (
        <>
            <header
                data-testid="site-header"
                className={`sticky top-0 z-40 transition-all duration-300 ${bgClass}`}
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16 sm:h-20 gap-4">
                        <HeaderBrand info={info} />
                        <HeaderDesktopNav />
                        <HeaderActions
                            info={info}
                            open={open}
                            onToggle={() => setOpen((v) => !v)}
                        />
                    </div>
                </div>
            </header>
            <SiteHeaderMobileMenu
                open={open}
                onClose={() => setOpen(false)}
                links={MOBILE_LINKS}
                info={info}
            />
        </>
    );
};

export default SiteHeader;
